import { useCallback, useMemo } from "react";
import { useApp } from "../context/AppContext";
import { useSalon } from "../context/SalonContext";
import { API_CONFIG, getApiUrl } from "../config/api";
import {
  AssignWaitlistEntryRequest,
  AssignWaitlistEntryResponse,
  CreateWaitlistEntryRequest,
  UpdateWaitlistEntryRequest,
  WaitlistEntry,
} from "../types/waitlist";

const listFrom = (response: unknown): WaitlistEntry[] => {
  const rows = (response as { waitlist?: unknown } | null)?.waitlist;
  return Array.isArray(rows) ? (rows as WaitlistEntry[]) : [];
};

/**
 * Walk-in waitlist API (core, staff auth), every call through
 * `fetchWithAuth`. Scoped to the selected business location
 * (`useSalon().businessInfo.id`). Self-contained like useAppointmentTags.
 */
export function useWaitlistApi() {
  const { fetchWithAuth } = useApp();
  const { businessInfo } = useSalon() as { businessInfo: { id?: string } };
  const businessLocationId = businessInfo?.id;

  /** Today's waiting rows in line order (the backend drops other days). */
  const listWaitlist = useCallback(async (): Promise<WaitlistEntry[]> => {
    if (!businessLocationId) return [];
    const response = await fetchWithAuth(
      getApiUrl(API_CONFIG.ENDPOINTS.WAITLIST, "", { businessLocationId }),
    );
    return listFrom(response);
  }, [fetchWithAuth, businessLocationId]);

  /** Add a walk-in at the end of the line. */
  const createEntry = useCallback(
    async (data: CreateWaitlistEntryRequest): Promise<WaitlistEntry> => {
      if (!businessLocationId) throw new Error("No business location selected");
      return fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.WAITLIST, "", { businessLocationId }),
        { method: "POST", body: JSON.stringify(data) },
      );
    },
    [fetchWithAuth, businessLocationId],
  );

  /** Partial edit (client / service / comments). */
  const updateEntry = useCallback(
    async (
      waitlistId: string,
      data: UpdateWaitlistEntryRequest,
    ): Promise<WaitlistEntry> =>
      fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.WAITLIST_BY_ID, "", { waitlistId }),
        { method: "PUT", body: JSON.stringify(data) },
      ),
    [fetchWithAuth],
  );

  /** Full new order; resolves to the server's list. */
  const reorderWaitlist = useCallback(
    async (uuids: string[]): Promise<WaitlistEntry[]> => {
      if (!businessLocationId) throw new Error("No business location selected");
      const response = await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.WAITLIST_ORDER, "", {
          businessLocationId,
        }),
        { method: "PUT", body: JSON.stringify({ uuids }) },
      );
      return listFrom(response);
    },
    [fetchWithAuth, businessLocationId],
  );

  /** Soft remove (status → removed). */
  const removeEntry = useCallback(
    async (waitlistId: string): Promise<void> => {
      await fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.WAITLIST_BY_ID, "", { waitlistId }),
        { method: "DELETE" },
      );
    },
    [fetchWithAuth],
  );

  /** Book the walk-in as an appointment (row leaves the list). */
  const assignEntry = useCallback(
    async (
      waitlistId: string,
      data: AssignWaitlistEntryRequest,
    ): Promise<AssignWaitlistEntryResponse> =>
      fetchWithAuth(
        getApiUrl(API_CONFIG.ENDPOINTS.WAITLIST_ASSIGN, "", { waitlistId }),
        { method: "POST", body: JSON.stringify(data) },
      ),
    [fetchWithAuth],
  );

  return useMemo(
    () => ({
      businessLocationId,
      listWaitlist,
      createEntry,
      updateEntry,
      reorderWaitlist,
      removeEntry,
      assignEntry,
    }),
    [
      businessLocationId,
      listWaitlist,
      createEntry,
      updateEntry,
      reorderWaitlist,
      removeEntry,
      assignEntry,
    ],
  );
}

export type WaitlistApi = ReturnType<typeof useWaitlistApi>;
